import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatPaginatorComponent } from '../../Components/mat-paginator/mat-paginator.component';
import { LoaderService } from '../../Services/Common/loader.service';

@Component({
  selector: 'app-tax-compliance-certificate',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatPaginatorComponent],
  templateUrl: './tax-compliance-certificate.component.html',
  styleUrl: './tax-compliance-certificate.component.scss'
})
export class TaxComplianceCertificateComponent {
  selectedCaseId = '';
  searchText = '';
  pageIndex = 0;
  readonly pageSize = 5;
  private readonly originalTableRows: string[][];

  readonly tableData = {
    tableType: 'sorting',
    tableHeadings: [
      'Case ID',
      'Reason For Request',
      'Contract Payment Value',
      'Status',
      'Validity Period',
      'Request Date',
      'Issue Date',
      'Expiry Date',
      'Action'
    ],
    tableData: [
      ['TCC_REG_NEW_001', 'Contracts Only', 'Less than $ 10,000', 'APP_DRAFT', '-', '1/1/2026', '-', '-', 'Continue'],
      ['TCC_REG_NEW_002', 'Multipurpose', 'more than $ 10,000', 'APP_PSUB', '-', '1/1/2026', '-', '-', '-'],
      ['TCC_REG_NEW_003', 'Multipurpose', 'more than $ 10,000', 'APP_SUBMIT', '-', '1/1/2026', '-', '-', '-'],
      ['TCC_REG_NEW_004', 'Multipurpose', 'more than $ 10,000', 'APPR_APP', '1 Month', '1/1/2026', '1/1/2026', '1/1/2027', 'View/Download'],
      ['TCC_REG_NEW_005', 'Multipurpose', 'more than $ 10,000', 'REJ_APP', '-', '1/1/2026', '-', '-', '-'],
      ['TCC_REG_NEW_006', 'Multipurpose', 'more than $ 10,000', 'APP_DRAFT', '-', '1/1/2026', '-', '-', 'Continue'],
      ['TCC_REG_NEW_007', 'Multipurpose', 'more than $ 10,000', 'APP_SUBMIT', '-', '1/1/2026', '-', '-', '-'],
    ],
    tableUrls: [
      ['/taxpayer/taxCompliance?caseId=TCC_REG_NEW_001', '', '', '', '', '', '', '', '/taxpayer/taxCompliance?caseId=TCC_REG_NEW_001'],
      ['/taxpayer/taxCompliance?caseId=TCC_REG_NEW_002', '', '', '', '', '', '', '', ''],
      ['/taxpayer/taxCompliance?caseId=TCC_REG_NEW_003', '', '', '', '', '', '', '', '/taxpayer/taxCompliance?caseId=TCC_REG_NEW_003'],
      ['/taxpayer/taxCompliance?caseId=TCC_REG_NEW_004', '', '', '', '', '', '', '', ''],
      ['/taxpayer/taxCompliance?caseId=TCC_REG_NEW_005', '', '', '', '', '', '', '', ''],
      ['/taxpayer/taxCompliance?caseId=TCC_REG_NEW_006', '', '', '', '', '', '', '', ''],
      ['/taxpayer/taxCompliance?caseId=TCC_REG_NEW_007', '', '', '', '', '', '', '']
    ]
  };

  constructor(private readonly router: Router, private readonly route: ActivatedRoute,
    private readonly loaderService: LoaderService) {
    this.originalTableRows = [...this.tableData.tableData];
    this.route.queryParamMap
      .pipe(takeUntilDestroyed())
      .subscribe(params => this.selectedCaseId = params.get('caseId') || '');
  }

  requestCertificate(): void {
    this.router.navigate(['/taxpayer/taxCompliance/request', 1]);
  }

  get selectedRequest(): string[] | undefined {
    return this.tableData.tableData.find(row => row[0] === this.selectedCaseId);
  }

  openRequest(caseId: string, statusCode?: string): void {
    this.loaderService.show('Loading application...');
    this.router.navigate(['/taxpayer/taxCompliance/request', 2], {
      queryParams: { caseId, status: statusCode }
    });
  }

  searchPrediction(e: any): void {
    const rawValue = (e?.target?.value || '').toString();
    this.searchText = rawValue;
    const term = rawValue.trim().toLowerCase();
    this.pageIndex = 0;
    this.tableData.tableData = term
      ? this.originalTableRows.filter(row => row.some((value, index) => {
        const displayValue = index === 3 ? this.getStatusLabel(value) : value;
        return value?.toString().toLowerCase().includes(term)
          || displayValue.toLowerCase().includes(term);
      }))
      : [...this.originalTableRows];
  }

  clearText(): void {
    this.searchText = '';
    this.pageIndex = 0;
    this.tableData.tableData = [...this.originalTableRows];
  }

  onFilterChange(): void { }

  search(): void { }

  getStatusLabel(statusCode: string): string {
    const labels: Record<string, string> = {
      APP_DRAFT: 'Draft',
      APP_PSUB: 'Under Review',
      APP_SUBMIT: 'Submitted',
      APPR_APP: 'Approved',
      REJ_APP: 'Rejected'
    };
    return labels[statusCode.trim().toUpperCase()] || statusCode;
  }

  getStatusClass(statusCode: string): string {
    const classes: Record<string, string> = {
      APP_DRAFT: 'badge--default',
      APP_PSUB: 'badge--pending',
      APP_SUBMIT: 'badge--accepted',
      APPR_APP: 'badge--accepted',
      REJ_APP: 'badge--declined'
    };
    return classes[statusCode.trim().toUpperCase()] || 'badge--default';
  }

  isDraftStatus(statusCode: string): boolean {
    return statusCode.trim().toUpperCase() === 'APP_DRAFT';
  }

  isViewableStatus(statusCode: string): boolean {
    return ['APP_PSUB', 'APP_SUBMIT', 'REJ_APP'].includes(statusCode.trim().toUpperCase());
  }

  openRequestAction(event: Event, caseId: string, action: string): void {
    event.preventDefault();
    if (action !== '-') this.openRequest(caseId);
  }

  get paginatedRows(): string[][] {
    const start = this.pageIndex * this.pageSize;
    return this.tableData.tableData.slice(start, start + this.pageSize);
  }

  get currentPage(): number {
    return this.pageIndex + 1;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.tableData.tableData.length / this.pageSize));
  }

  get visiblePages(): number[] {
    return Array.from({ length: this.totalPages }, (_, index) => index + 1);
  }

  onPageChange(page: number): void {
    this.pageIndex = Math.max(0, Math.min(page - 1, this.totalPages - 1));
  }
}